import React, { useEffect, useMemo, useState, memo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiEye, FiEdit3, FiTrash2, FiInfo, FiTag } from "react-icons/fi";
import { FaRegCommentDots, FaChartBar } from "react-icons/fa";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListUl } from "@fortawesome/free-solid-svg-icons";
import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";

const ShowOnHomeCell = memo(({ testId, initial, patchRow }) => {
  const [isChecked, setIsChecked] = useState(
    initial === 1 || initial === "1" || initial === true
  );
  const [busy, setBusy] = useState(false);

  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    const next = !isChecked;
    setIsChecked(next); // optimistik
    setBusy(true);

    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/tests/update-show-home/${testId}`,
        { show_on_home: next ? 1 : 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      //  Listeyi baştan çekme — sadece o satırı lokalde güncelle
      patchRow?.(testId, { show_on_home: next ? 1 : 0 });
    } catch (err) {
      console.error("❌ Güncelleme hatası:", err);
      setIsChecked(!next); // geri al
      toast.error("Ana sayfa görünürlüğü güncellenemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="form-check form-switch text-start d-flex align-items-start justify-content-start"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggle}
        disabled={busy}
        style={{
          cursor: busy ? "not-allowed" : "pointer",
          width: "45px",
          height: "22px",
          transition: "0.3s ease-in-out",
        }}
      />
    </div>
  );
});

/* ---- Stateless switch ---- */
const PopularCell = memo(({ testId, initial, patchRow }) => {
  const [isChecked, setIsChecked] = useState(
    initial === 1 || initial === "1" || initial === true
  );
  const [busy, setBusy] = useState(false);

  const toggle = async (e) => {
    e.stopPropagation(); // satır click’leri switch’i bozmasın
    if (busy) return;

    const next = !isChecked;
    setIsChecked(next);          // optimistik
    setBusy(true);

    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/tests/update-popular/${testId}`,
        { is_popular: next ? 1 : 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      //  Listeyi baştan çekme — sadece o satırı lokalde güncelle
      patchRow?.(testId, { is_popular: next ? 1 : 0 });
    } catch (err) {
      console.error("❌ Popüler güncellenemedi:", err);
      setIsChecked(!next);       // geri al
      toast.error("Güncelleme başarısız oldu");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="form-check form-switch text-start d-flex align-items-start justify-content-start"
      onClick={(e) => e.stopPropagation()} // tedbir
      onKeyDown={(e) => e.stopPropagation()}
    >
      <input
        className="form-check-input custom-checkbox"
        type="checkbox"
        role="switch"
        checked={isChecked}
        onChange={toggle}
        disabled={busy}
        style={{
          cursor: busy ? "not-allowed" : "pointer",
          width: "45px",
          height: "22px",
          transition: "0.3s ease-in-out",
        }}
      />
    </div>
  );
});

const TestTable = () => {
  const [rows, setRows] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (k, d = "") => translations[k] || d;

  //  Sadece ilgili satırı lokalde güncelle
  const patchRow = (id, patch) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };

  const fetchTests = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/tests/list`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }
      );
      const data = (res.data?.data || []).map((it) => ({
        ...it,
        is_popular:
          it.is_popular === 1 || it.is_popular === "1" || it.is_popular === true ? 1 : 0,
        // (İstersen) show_on_home’u da normalize edebilirsin:
        // show_on_home:
        //   it.show_on_home === 1 || it.show_on_home === "1" || it.show_on_home === true ? 1 : 0,
        has_dual_main_result:
  it.has_dual_main_result === 1 || it.has_dual_main_result === "1" || it.has_dual_main_result === true ? 1 : 0,
      }));

      setRows(data);
    } catch (err) {
      console.error("❌ Test listesi alınamadı:", err);
    }
  };

  useEffect(() => { fetchTests(); }, []);

  const handleDelete = id => {
    confirmAlert({
      title: t("CONFIRM_DELETE", "Silmek istediğinize emin misiniz?"),
      message: t("DELETE_WARNING", "Bu işlem geri alınamaz."),
      buttons: [
        {
          label: t("YES", "Evet, sil"),
          onClick: async () => {
            try {
              await axios.delete(
                `${process.env.REACT_APP_API_BASE_URL}/api/tests/${id}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }
              );
              toast.success(t("DELETE_SUCCESS", "Test başarıyla silindi."));
              fetchTests();
            } catch (err) {
              console.error("❌ Silme hatası:", err);
              toast.error(t("DELETE_FAILED", "Silme işlemi başarısız oldu."));
            }
          }
        },
        { label: t("CANCEL", "İptal"), onClick: () => {} }
      ]
    });
  };

  const columns = useMemo(() => [
    { accessorKey: "id", header: () => "ID", size: 60 },
    {
      id: "image",
      header: () => t("TABLE_IMAGE", "Resim"),
      size: 80,
      cell: info => {
        const raw = info.row.original.image;
        if (!raw) return null;
        const url = raw.startsWith("http")
          ? raw
          : `${process.env.REACT_APP_API_BASE_URL}${raw}`;
        return (
          <img
            src={url}
            alt={info.row.original.name}
            style={{
              width: 60,
              height: 40,
              objectFit: "cover",
              borderRadius: 4
            }}
          />
        );
      }
    },
    { accessorKey: "name", header: () => t("TABLE_TEST_NAME", "Test Adı") },
    { accessorKey: "max_score", header: () => t("TABLE_MAX_SCORE", "Maks. Puan") },

  { accessorKey: "price", header: () => "Fiyat", size: 120 },
    {
      id: "is_popular",
      header: () => (
        <div style={{ textAlign: "start", width: 160 }}>
          {t("TABLE_IS_POPULAR", "Popüler")}
        </div>
      ),
      meta: { headerClassName: "text-start", className: "text-start" },
      size: 160,
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div style={{ textAlign: "start", width: 160 }}>
            <PopularCell
              testId={row.id}
              initial={row.is_popular}
              patchRow={patchRow}   
            />
          </div>
        );
      },
    },
    {
      id: "show_on_home",
      header: () => (
        <div style={{ textAlign: "start", width: 160 }}>
          {t("TABLE_SHOW_ON_HOME", "Anasayfada Görünsün mü?")}
        </div>
      ),
      meta: { headerClassName: "text-start", className: "text-start" },
      size: 160,
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div style={{ textAlign: "start", width: 160 }}>
            <ShowOnHomeCell
              testId={row.id}
              initial={row.show_on_home}
              patchRow={patchRow}   
            />
          </div>
        );
      },
    },
       {
     accessorKey: "question_count",
     header: () => t("TABLE_QUESTION_COUNT", "Soru Sayısı"),
     size: 120,
     cell: (info) => <span>{Number(info.getValue() || 0)}</span>,
   },

    {
  accessorKey: "created_at",
  header: () => (
    <div style={{ textAlign: "start", paddingLeft: "8px" }}>
      {t("TABLE_DATE", "Tarih")}
    </div>
  ),
  meta: { headerClassName: "text-start", className: "text-start" },
  size: 120, // daraltmak için
  cell: (info) => (
    <div style={{ textAlign: "start", paddingRight: "40px" }}>
      {new Date(info.getValue()).toLocaleDateString("tr-TR")}
    </div>
  ),
},
{
  accessorKey: "actions",
  header: () => t("ACTIONS", "İşlemler"),
  enableSorting: false,
  cell: (info) => {
    const { id, has_category, has_dual_main_result } = info.row.original;

    // Ana butonu koşula göre hazırla
    let mainButton = null;
    if (has_dual_main_result) {
      // 👇 İkili Ana Sonuç sayfasına giden buton
      mainButton = (
        <Link
          to={`/test/dual-results/${id}`}
          className="avatar-text avatar-md text-secondary bg-soft-secondary border-soft-secondary"
          title={t("DUAL_RESULTS", "İkili Sonuç Sayfası")}
        >
          <FaRegCommentDots />
        </Link>
      );
    } else if (has_category) {
      mainButton = (
        <Link
          to={`/test/${id}/category/list`}
          className="avatar-text avatar-md text-secondary bg-soft-secondary border-soft-secondary"
          title={t("CATEGORY", "Kategori")}
        >
          <FiTag />
        </Link>
      );
    } else {
      mainButton = (
        <Link
          to={`/test/score/table/${id}`}
          className="avatar-text avatar-md text-info bg-soft-info border-soft-info"
          title={t("SCORE_EXPLAINS", "Puan Açıklamaları")}
        >
          <FaChartBar />
        </Link>
      );
    }

    return (
      <div className="hstack gap-2 justify-content-end">
        {mainButton}

        <Link
          to={`/test/questions/${id}`}
          className="avatar-text avatar-md text-warning bg-soft-warning border-soft-warning"
          title={t("QUESTIONS", "Sorular")}
        >
          <FontAwesomeIcon icon={faListUl} />
        </Link>

        <Link
          to={`/test/view/${id}`}
          className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
          title={t("VIEW", "Görüntüle")}
        >
          <FiEye />
        </Link>

        <Link
          to={`/test/edit/${id}`}
          className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
          title={t("EDIT", "Düzenle")}
        >
          <FiEdit3 />
        </Link>

        <button
          type="button"
          onClick={() => handleDelete(id)}
          className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
          title={t("DELETE", "Sil")}
        >
          <FiTrash2 />
        </button>
      </div>
    );
  },
}

  ], [t]);

  return <Table data={rows} columns={columns} />;
};

export default TestTable;
